import { MiniAction } from '@otto-ec/tracking-bct';
import { BCT_TRACKING_SUBMIT_MINI_ACTIONS } from '../../_common/client/bct-event-topics';
import { emitTrackingEvent } from '../../_common/client/tracking/emit-tracking-event';
import { TrackingFeatures } from './event-data-types';

export const DATA_ATTRIBUTE_VISIBILITY_TRACKING = 'data-ft5-visibility-tracking';
const VISIBILITY_FEATURE_PARENT_ID = 'VisibilityFeatureParentId';

export type VisibilityTrackingOptions = {
    componentName: string;
    intersectionThreshold: number;
};

export class VisibilityTracking {
    public static readonly DEFAULT_THRESHOLD = 0.75;

    public submitMiniActionWithVariationId(variationId: string) {
        emitTrackingEvent(BCT_TRACKING_SUBMIT_MINI_ACTIONS, [{ featureId: variationId, action: 'scroll' }]);
    }

    public getVisibilityTrackingData(): TrackingFeatures | undefined {
        const visibilityTrackingTargets = this.findVisibilityTrackingTargets();
        const componentsVisibilityTrackingData: TrackingFeatures = [];
        for (let i = 0; i < visibilityTrackingTargets.length; i++) {
            const visibilityTrackingOptions = this.getVisibilityTrackingOptions(visibilityTrackingTargets[i]);
            if (!visibilityTrackingOptions) {
                continue;
            }

            componentsVisibilityTrackingData.push({
                id: visibilityTrackingOptions.componentName,
                name: visibilityTrackingOptions.componentName,
                position: i + 1,
                parentId: VISIBILITY_FEATURE_PARENT_ID,
                status: 'loaded',
                labels: {},
            });
        }

        if (componentsVisibilityTrackingData && componentsVisibilityTrackingData.length > 0) {
            return [
                {
                    id: VISIBILITY_FEATURE_PARENT_ID,
                    name: 'ProductDetailPage-Components',
                    status: 'loaded',
                    labels: {},
                },
                ...componentsVisibilityTrackingData,
            ];
        }

        return undefined;
    }

    public registerIntersectionObserver(): void {
        const visibilityTrackingTargets = this.findVisibilityTrackingTargets();
        const thresholds = this.getThresholdsFromTargets(visibilityTrackingTargets);
        const oberserver = this.createIntersectionObserver(thresholds);

        for (let i = 0; i < visibilityTrackingTargets.length; i++) {
            oberserver.observe(visibilityTrackingTargets[i]);
        }
    }

    private findVisibilityTrackingTargets(): NodeListOf<HTMLElement> {
        return document.querySelectorAll(`[${DATA_ATTRIBUTE_VISIBILITY_TRACKING}]`);
    }

    private createIntersectionObserver(thresholds: Array<number>): IntersectionObserver {
        const observerOptions: IntersectionObserverInit = {
            threshold: thresholds,
        };

        return new IntersectionObserver(
            (entries, observer) => this.trackScrolledVisibilityAndUnobserveTarget(entries, observer),
            observerOptions
        );
    }

    private trackScrolledVisibilityAndUnobserveTarget(entries: IntersectionObserverEntry[], observer: IntersectionObserver): void {
        const trackingData: Array<MiniAction> = [];
        entries.forEach(entry => {
            const observedElement = entry.target as HTMLElement;
            const options = this.getVisibilityTrackingOptions(observedElement);
            if (!options) {
                return;
            }

            if (this.isIntersecting(entry, options.intersectionThreshold) && this.isVisible(observedElement)) {
                const featureId = options.componentName;

                trackingData.push({ featureId, action: 'scroll', status: 'visible' });
                observer.unobserve(observedElement);
            }
        });

        if (trackingData.length > 0) {
            emitTrackingEvent(BCT_TRACKING_SUBMIT_MINI_ACTIONS, trackingData);
        }
    }

    private isIntersecting(entry: IntersectionObserverEntry, intersectionThreshold: number) {
        return entry.isIntersecting && entry.intersectionRatio >= intersectionThreshold;
    }

    private isVisible(element: HTMLElement): boolean {
        try {
            const isVisibleByCss = element.offsetParent !== null;
            const isVisibleByClientHeight = element.clientHeight > 0;

            return isVisibleByCss && isVisibleByClientHeight;
        } catch (error) {
            return false;
        }
    }

    private getThresholdsFromTargets(visibilityTrackingTargets: NodeListOf<HTMLElement>): Array<number> {
        const uniqueThresholds = new Set<number>();

        visibilityTrackingTargets.forEach(target => {
            const options = this.getVisibilityTrackingOptions(target);
            if (options) {
                uniqueThresholds.add(options.intersectionThreshold);
            }
        });

        return Array.from(uniqueThresholds);
    }

    private getVisibilityTrackingOptions(target: HTMLElement): VisibilityTrackingOptions | null {
        const optionsJson = target.getAttribute(DATA_ATTRIBUTE_VISIBILITY_TRACKING);
        try {
            const options = JSON.parse(optionsJson!) as VisibilityTrackingOptions;
            if (options.intersectionThreshold) {
                options.intersectionThreshold = Number(options.intersectionThreshold);
            }
            if (!options.intersectionThreshold) {
                options.intersectionThreshold = VisibilityTracking.DEFAULT_THRESHOLD;
            }
            return options;
        } catch (_err) {
            return null;
        }
    }
}
